'use strict';

import React from 'react';
import moment from 'moment-timezone';

import Badge from 'react-bootstrap/Badge';

import TermUtils from '@uw-it-sis/lib-js-common/lib/TermUtils';

import {SeattleTimeZone} from './AppConstants';
import { isFalse, isEmpty } from './Utils';

/*
 * Add in React specific functions to the shared TermUtils object.
 */

/**
 * Makes a term badge (actually a label at the moment).
 *
 * @param term A term description (e.g. "Winter 2018")
 * @returns A Label containing a shortened term description (e.g. "WI 18")
 */
TermUtils.makeTermBadge = (term, i) => {
    let termClass = _getTermClass(term);
    let termLabel = TermUtils._getTermShortName(term);
    return <Badge key={i} bg={termClass} className="text-dark fw-bold"><abbr title={term} aria-hidden={true}>{termLabel}</abbr><span className="sr-only">{term}</span></Badge>;
};


/**
 * Makes a term abbreviation tag from a term description.
 *
 * @param term A term description (e.g. "Winter 2016")
 * @returns An abbr tag containing a shortened term description (e.g. "WI 18")
 */
TermUtils.makeTermAbbr = (term, i) => {
    let termLabel = TermUtils._getTermShortName(term);
    return <abbr key={i} title={term} aria-label={term}>{termLabel.replace(/\s/g,'')}</abbr>;
};

/**
 * Makes a term abbreviation tag from a term ID.
 *
 * @param termId termId An atpId (e.g. "20164")
 * @returns An abbr tag containing a shortened term description (e.g. "WI 18")
 */
TermUtils.makeTermAbbrFromTermId = (termId, i) => {
    // Check that the termId provide is valid, if not return null
    if ( isFalse(TermUtils.isValidFormattedTermId(termId)) ) {
        return null;
    }

    let termDescription = TermUtils._makeTermDescription(termId);
    let termLabel = TermUtils._getTermShortName(termDescription);
    return <abbr key={i} title={termDescription} aria-label={termDescription}>{termLabel}</abbr>;
};

/**
 * Makes a term React Bootstrap component badge.
 *
 * @param termId An atpId (e.g. "20164")
 * @returns A term badge React component.
 */
TermUtils.makeTermBadgeFromTermId = (termId, i) => {
    // Check that the termId provide is valid, if not return null
    if ( isFalse(TermUtils.isValidFormattedTermId(termId)) ) {
        return null;
    }

    let termDescription = TermUtils._makeTermDescription(termId);
    return TermUtils.makeTermBadge(termDescription, i);
};

/**
 * Determines the CSS class that should be associated with the term.
 *
 * @param term A string of a term description (e.g. "Winter 2018", "Winter")
 * @returns A string including the term description (minus any year) in lowercase prefixed with 'bg-' to be used as a CSS class name (e.g. "bg-winter")
 * @private
 */
const _getTermClass = (term) => {
    return term.split(" ")[0].toLowerCase();
};

/**
 * Creates an array of terms sorted by the academic year (i.e. autumn, winter, spring, summer)
 * @type {string[]}
 */
TermUtils.sortedList = Object.entries(TermUtils.TERMS).sort((a,b) => {
    if (parseInt(a[0]) === 4) {
        return -1
    } else {
        return a[0]-b[0]
    }
}).map(([id, name], index) => name.toLowerCase());

/**
 * Takes a termId and based on the number of terms calculates a new term id
 * @param termId - starting point from when to calculate the new term id
 * @param terms - number of terms to shift from a given term id
 */
TermUtils.calcNewTermId = (termId, terms) => {
    const termNumber = parseInt(termId.substring(4, 5)); // Get the term number from the 5th character
    const termYear = parseInt(termId.substring(0, 4)); // Get the year from first 4 characters
    const yearShift = Math.floor(Math.abs(terms) / 4);
    const termShift = (Math.abs(terms) % 4);

    let newYear, newTerm;

    if (parseInt(terms) === 0) {
        newYear = termYear;
        newTerm = termNumber;
    } else if (parseInt(terms) > 0) {
        newYear = termYear + yearShift;
        newTerm = termNumber + termShift;
        if (newTerm > 4) {
            newYear++;
            newTerm = newTerm % 4;
        }
    } else if (parseInt(terms) < 0) {
        newYear = termYear - yearShift;
        newTerm = termNumber - termShift;
        if (newTerm < 1) {
            newYear--;
            newTerm = (newTerm % 4) + 4;
        }
    }

    return `${newYear}${newTerm}`;
}

/**
 * Test to see if "now" is after, before, or between dates from within the academic calendar using the moment library
 * @param termData - a term data object that contains academic calendar data
 * @param academicCalendarKey - prop of the academic calendar date entry to check against.
 * "between" can either be between two academic calendar keys (passed as list of the two keys)
 * or a single key if it has a start and end date specified.
 * @param type - Options are "after", "before", "between"
 * @param dateNow - date now time, can be actual 'now' or set to a value via now widget
 * @param shift - object of time shift; key being the period to shift and the value being the number, only works for before or after. For keys: https://momentjs.com/docs/#/manipulating/add/
 * @param isDevMode - boolean flag for displaying console output and warnings
 *
 * @returns {*|boolean}
 */

TermUtils.isNow = (termData, academicCalendarKey, type, dateNow = Date.now(), isDevMode = false, shift = {"d": 0}) => {
    const now = moment.tz( new Date( dateNow ), SeattleTimeZone);
    let result = false;
    let test;
    // check to see if term data is present and prop to look for is specified
    if ( isFalse(isEmpty(termData)) && termData.academicCalendar && termData.academicCalendar.length > 0 && isFalse(isEmpty(academicCalendarKey)) ) {
        let academicCalendarItem;
        if (type === "between" && typeof academicCalendarKey === 'object') {
            // if a list of keys passed, find those keys in academic calendar
            academicCalendarItem = termData.academicCalendar.filter(i => academicCalendarKey.includes(i.key));
            // warn if the filter does not return exactly 2 calendar dates from the academic calendar
            if (isDevMode && academicCalendarItem.length !== 2) console.warn(`[TermUtils] isNow: 'between' keys did not find exactly 2 academic calendar dates`);
        } else {
            academicCalendarItem = termData.academicCalendar.find(i => i.key === academicCalendarKey);
        }

        if (!shift) { shift = {"d": 0} }
        let shiftPeriod = Object.keys(shift)[0], shiftNum = Number(Object.values(shift)[0]);

        if (academicCalendarItem) {
            switch (type) {
                case "before":
                    // set the test to the academic calendar date 12:00:00am Seattle time (beginning of day)
                    test = moment.tz(academicCalendarItem.date, SeattleTimeZone).startOf("day").add(shiftNum, shiftPeriod) ?? ""
                    result = now.isBefore(test);
                    break;
                case "after":
                    // set the test to the academic calendar date 11:59:59pm Seattle time (end of day)
                    test = moment.tz(academicCalendarItem.date, SeattleTimeZone).endOf("day").add(shiftNum, shiftPeriod) ?? ""
                    result = now.isAfter(test);
                    break;
                case "between":
                    if (typeof academicCalendarKey === 'object') {
                        // build an array of dates
                        test = academicCalendarItem.map((d, index) => {
                            // create moment object from academic calendar date
                            let academicCalendarDateMomentObj = moment.tz(d.date, SeattleTimeZone);
                            // if it's the first index, use start of day; if it's the last, use end of day
                            return index === 0 ? academicCalendarDateMomentObj.startOf("day") : academicCalendarDateMomentObj.endOf("day");
                        })
                    } else {
                        if (academicCalendarItem.hasOwnProperty("range") && academicCalendarItem.range) {
                            test = [
                                // the start date
                                moment.tz(academicCalendarItem.date, SeattleTimeZone).startOf("day") ?? "",
                                // the end date
                                moment.tz(academicCalendarItem.endDate, SeattleTimeZone).endOf("day") ?? ""
                            ];
                        }
                    }
                    result = now.isBetween(test[0], test[1])
                    break;
            }
        } else {
            if (isDevMode) console.warn(`[TermUtils] isNow: did not find academic calendar date(s)`);
        }

        if (isDevMode) {
            let dateFormat = "M/D/YY HH:mm";
            console.log(`[TermUtils] ${TermUtils.makeTermDescriptionFromTermId(termData.id)}: now (${now.format(dateFormat)}) is ${type} ${academicCalendarKey} ${test ? `(${test.format(dateFormat)}${shiftNum !== 0 ? ` shifted by ${JSON.stringify(shift)}`:''})` : ''}: ${String(result).toUpperCase()}`);
        }
    }

    return result;
}

export default TermUtils;